import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { cancellationService } from '../../services/cancellationService';
import { AlertCircle, CheckCircle, Filter, CheckSquare, Square } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import CancellationRequestCard from './components/CancellationRequestCard';
import DecisionModal from './components/DecisionModal';
import StatsOverview from './components/StatsOverview';
import FilterPanel from './components/FilterPanel';
import BulkActionModal from './components/BulkActionModal';

export default function CancellationRequestManagement() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pendingRequests: 0, processedToday: 0, totalCancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisionType, setDecisionType] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Filter and selection states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    dateFrom: '',
    dateTo: '',
    refundMin: '',
    refundMax: ''
  });
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRequestIds, setSelectedRequestIds] = useState(new Set());
  const [bulkActionType, setBulkActionType] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadCancellationRequests();
      loadStats();
    }
  }, [user, filters]);

  const loadCancellationRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await cancellationService?.getOrganizerCancellationRequestsWithFilters(user?.id, filters);
      setRequests(data);
    } catch (err) {
      setError(err?.message || 'Failed to load cancellation requests');
      console.error('Error loading cancellation requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await cancellationService?.getCancellationStats(user?.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setDecisionType('approve');
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setDecisionType('reject');
  };

  const handleConfirmDecision = async (organizerNotes) => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      setError('');

      if (decisionType === 'approve') {
        await cancellationService?.approveCancellation(selectedRequest?.id, organizerNotes);
      } else {
        await cancellationService?.rejectCancellation(selectedRequest?.id, organizerNotes);
      }

      // Reload data
      await loadCancellationRequests();
      await loadStats();

      // Close modal
      setSelectedRequest(null);
      setDecisionType(null);
    } catch (err) {
      setError(err?.message || `Failed to ${decisionType} cancellation request`);
      console.error('Error processing cancellation:', err);
    } finally {
      setProcessing(false);
    }
  };

  const calculateRefundAmount = (request) => {
    return cancellationService?.calculateRefundAmount(request);
  };

  // Add this line - Move filteredRequests declaration before it's used
  const filteredRequests = requests;

  // Selection handlers
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedRequestIds(new Set());
  };

  const handleSelectAll = () => {
    if (selectedRequestIds?.size === filteredRequests?.length) {
      setSelectedRequestIds(new Set());
    } else {
      setSelectedRequestIds(new Set(filteredRequests?.map(r => r?.id)));
    }
  };

  const handleSelectionChange = (requestId, isSelected) => {
    const newSelection = new Set(selectedRequestIds);
    if (isSelected) {
      newSelection?.add(requestId);
    } else {
      newSelection?.delete(requestId);
    }
    setSelectedRequestIds(newSelection);
  };

  const handleBulkAction = (actionType) => {
    if (selectedRequestIds?.size === 0) {
      setError('Please select at least one request');
      return;
    }
    setBulkActionType(actionType);
  };

  const handleConfirmBulkAction = async (organizerNotes) => {
    try {
      setProcessing(true);
      setError('');

      const selectedIds = Array.from(selectedRequestIds);
      let results;

      if (bulkActionType === 'approve') {
        results = await cancellationService?.bulkApproveCancellations(selectedIds, organizerNotes);
      } else {
        results = await cancellationService?.bulkRejectCancellations(selectedIds, organizerNotes);
      }

      // Show results
      if (results?.failed > 0) {
        setError(`Processed ${results?.successful} requests successfully, ${results?.failed} failed`);
      }

      // Reload data
      await loadCancellationRequests();
      await loadStats();

      // Reset selection
      setSelectedRequestIds(new Set());
      setBulkActionType(null);
      setSelectionMode(false);
    } catch (err) {
      setError(err?.message || `Failed to process bulk ${bulkActionType}`);
      console.error('Error processing bulk action:', err);
    } finally {
      setProcessing(false);
    }
  };

  const selectedRequestObjects = requests?.filter(r => selectedRequestIds?.has(r?.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Cancellation Request Management
          </h1>
          <p className="text-gray-600">
            Review and process participant cancellation requests for your events
          </p>
        </div>

        {/* Stats Overview */}
        <StatsOverview stats={stats} statistics={stats} />

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Left side - Filters and Selection */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Filter Button */}
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {(filters?.status !== 'all' || filters?.dateFrom || filters?.dateTo || filters?.refundMin || filters?.refundMax) && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    Active
                  </span>
                )}
              </button>

              {/* Selection Mode Toggle */}
              <button
                onClick={handleToggleSelectionMode}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectionMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {selectionMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                <span>{selectionMode ? 'Exit Selection' : 'Select Multiple'}</span>
              </button>

              {/* Select All (shown in selection mode) */}
              {selectionMode && (
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedRequestIds?.size === filteredRequests?.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {/* Right side - Bulk Actions */}
            {selectionMode && selectedRequestIds?.size > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 font-medium">
                  {selectedRequestIds?.size} selected
                </span>
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Bulk Approve</span>
                </button>
                <button
                  onClick={() => handleBulkAction('reject')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Bulk Reject</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mb-6">
            <FilterPanel
              filters={filters}
              onFiltersChange={(newFilters) => {
                setFilters(newFilters);
                setShowFilterPanel(false);
              }}
              onClose={() => setShowFilterPanel(false)}
            />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Requests List */}
        {filteredRequests?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Requests Found
              </h3>
              <p className="text-gray-600">
                No cancellation requests match your current filters.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests?.map(request => (
              <CancellationRequestCard
                key={request?.id}
                request={request}
                onApprove={handleApprove}
                onReject={handleReject}
                calculateRefundAmount={calculateRefundAmount}
                selectionMode={selectionMode}
                isSelected={selectedRequestIds?.has(request?.id)}
                onSelectionChange={handleSelectionChange}
              />
            ))}
          </div>
        )}

        {/* Single Decision Modal */}
        {selectedRequest && decisionType && (
          <DecisionModal
            request={selectedRequest}
            decisionType={decisionType}
            processing={processing}
            onConfirm={handleConfirmDecision}
            onCancel={() => {
              setSelectedRequest(null);
              setDecisionType(null);
            }}
            calculateRefundAmount={calculateRefundAmount}
          />
        )}

        {/* Bulk Action Modal */}
        {bulkActionType && (
          <BulkActionModal
            selectedRequests={selectedRequestObjects}
            actionType={bulkActionType}
            processing={processing}
            onConfirm={handleConfirmBulkAction}
            onCancel={() => setBulkActionType(null)}
          />
        )}
      </div>
    </div>
  );
}